import callAPI from './call-api';
import { List } from 'api/list/graphql/fragments/ListFields';
import {
    listEndpoint as endpoint,
    language,
    listClientId,
} from 'utils/settings';
import getToken from 'utils/auth';
import type {
    MinimalList,
    AddItemsMutationVariables,
    RemoveItemsMutationVariables,
    CreateListMutationVariables,
    SetSelectedMutationVariables,
    AddDesignsMutationVariables,
    RemoveDesignsMutationVariables,
    GetListsExtendedQuery,
} from 'api/list/graphqlTypes';
import {
    AddItems,
    CreateList,
    GetLists,
    GetMinimalListsQuery,
    RemoveItems,
    SetSelected,
} from './graphql';

import AddDesigns from './graphql/mutations/AddDesigns';
import RemoveDesigns from './graphql/mutations/RemoveDesigns';
import GetListsExtended from './graphql/queries/GetListsExtended';

type ListError = {
    code: string;
    data?: Object;
};
type ServerResponse<T> = {
    data: T;
    errors?: {
        extensions?: ListError;
    }[];
};

export const FAVOURITES_IGNORED_ERROR_CODES = ['ITEM_NUMBER_NOT_FOUND'];
const FAVOURITES_GROUPED_ERROR_CODES = [
    'INVALID_ITEM_NUMBER',
    'ILLEGAL_QUANTITY',
];

export const isWhiteListedError = (err: Error) =>
    FAVOURITES_IGNORED_ERROR_CODES.some(errorCode =>
        err.message.includes(errorCode)
    );

const _callApi = function <T>({
    query,
    variables,
    clientId,
}: {
    query: string;
    variables?: Object;
    clientId: string;
}): Promise<ServerResponse<T>> {
    return getToken().then(token =>
        callAPI({ query, variables, token, clientId })
            .catch(e => {
                console.error(e);
                return Promise.reject(e);
            })
            .then(response => response.json())
            .then((res: ServerResponse<T>) => {
                function getErrorDetails() {
                    return (res.errors ?? []).map(error => ({
                        code: String(error.extensions?.code),
                        data: error.extensions?.data,
                    }));
                }
                if (res.errors && res.errors.length > 0) {
                    const errorDetails = getErrorDetails();

                    // Multiple errors
                    if (errorDetails.length > 1) {
                        const errorList = errorDetails
                            .map(({ code }) => code)
                            .join(', ');
                        return Promise.reject(
                            new Error(`Multiple errors: ${errorList}`)
                        );
                    } else {
                        const { code, data } = errorDetails[0];
                        // This error should be grouped. Don't attach the data to the error name.
                        if (FAVOURITES_GROUPED_ERROR_CODES.includes(code)) {
                            return Promise.reject(code);
                        }
                        return Promise.reject(
                            new Error(
                                `Got ${code} when contacting ${endpoint}. Error data: ${JSON.stringify(
                                    data
                                )}`
                            )
                        );
                    }
                }
                return res;
            })
    );
};
export function addItems(
    variables: AddItemsMutationVariables,
    clientId = listClientId
) {
    return _callApi<{ list: List }>({
        query: AddItems(language),
        variables,
        clientId,
    });
}

export function removeItems(
    variables: RemoveItemsMutationVariables,
    clientId = listClientId
) {
    return _callApi<{ list: List }>({
        query: RemoveItems(language),
        variables,
        clientId,
    });
}

export function getLists(clientId = listClientId) {
    return _callApi<{ lists: List[] }>({ query: GetLists(language), clientId });
}

export function getListsExtended(storeId?: string) {
    const clientId = listClientId;
    return _callApi<{ lists: GetListsExtendedQuery['lists'] }>({
        query: GetListsExtended(language),
        variables: {
            storeId: storeId || null,
            includeStoreData: Boolean(storeId),
        },
        clientId,
    });
}

export function GetMinimalLists(clientId = listClientId) {
    return _callApi<{
        lists: MinimalList[];
    }>({ query: GetMinimalListsQuery, clientId });
}

export function createList(
    variables: CreateListMutationVariables,
    clientId = listClientId
) {
    return _callApi<{ list: List }>({
        query: CreateList,
        variables,
        clientId,
    });
}
export function setSelected(
    variables: SetSelectedMutationVariables,
    clientId = listClientId
) {
    return _callApi<{ list: List[] }>({
        query: SetSelected(language),
        variables,
        clientId,
    });
}

export function addDesigns(
    variables: AddDesignsMutationVariables,
    clientId = listClientId
) {
    return _callApi<{ list: List }>({
        query: AddDesigns(language),
        variables,
        clientId,
    });
}

export function removeDesigns(
    variables: RemoveDesignsMutationVariables,
    clientId = listClientId
) {
    return _callApi<{ list: List }>({
        query: RemoveDesigns(language),
        variables,
        clientId,
    });
}
